import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, LOCALE_ID, ElementRef, input, output, inject, viewChild, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators, AbstractControl, UntypedFormControl, UntypedFormGroup, UntypedFormArray, FormsModule, ValidationErrors, ValidatorFn } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DatePipe, AsyncPipe, NgClass } from '@angular/common';
import { Store } from '@ngrx/store';
import { COMPANY_INFO, COUNTRY_MASTER_STATE_LIST, SERVICETYPE_LIST_STATE, STAFF_STATE, TENANTS_INFO } from '@fitness-force/state';
import { BehaviorSubject, catchError, debounceTime, delay, distinctUntilChanged, finalize, forkJoin, map, Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { CountryISO, NgxIntlTelInputComponent, PhoneNumberFormat } from '@fitness-force/fitness-int-phone-code';
import { CONSTANTS_DATA } from '@fitness-force/constants';
import { ClientGender, ClientChannel, OpportunityStageForNonMember, OpportunityBoardType, TaskPriority, TaskAssignToType, OpportunitiesType, ClientRelation, ElasticProviderName, Roles } from '@fitness-force/enum';
import * as lpn from 'google-libphonenumber';
import { AdvancedSettingService, AuthSevices, ClientService, CommonService, OmniSearchService, OpportunityStageService, OrganizationService, TaskCategoryService, TimeconversionService, ToastMessageService } from '@fitness-force/api';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { DatePickerModule } from 'primeng/datepicker';
import { MiniSearchComponent } from '@fitness-force/common/search';
import { ErrorMsgComponent } from '@fitness-force/errors';;
import { NgxFileDropEntry, NgxFileDropModule } from 'ngx-file-drop';
import { AvatarModule } from 'primeng/avatar';
import { BlockUIModule } from 'primeng/blockui';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputMaskModule } from 'primeng/inputmask';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { RippleModule } from 'primeng/ripple';
import { StyleClassModule } from 'primeng/styleclass';
import { DrawerModule } from 'primeng/drawer';
import { AssignedToComponent } from '@fitness-force/common/assigned-to';
import type { CreateClientRequest, CreateOpportunityOnClientRequest, CreateProspectRequest, CreateProspectResponse, CreateTaskByOpportunityOnClientRequest, LANGUAGE, STATE_STAFF_LIST, ClientById, PlanSummaryCRM, PlanSummaryClient, PlanSummary } from '@fitness-force/models';
import { InputNumberModule } from 'primeng/inputnumber';
import { FeatureFlagService } from '@fitness-force/shared-utils';
import { SkeletonModule } from 'primeng/skeleton';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ImageCropDialogComponent } from '@fitness-force/common/crop';
import { WalkInTourComponent } from '../walk-in-tour/walk-in-tour.component';
import { WalkInTrialComponent } from '../walk-in-trial/walk-in-trial.component';
import { LostDealComponent } from '@fitness-force/crm';
import { MultiTenantListComponent } from '@fitness-force/common/multi-tenant-list';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { TextareaModule } from 'primeng/textarea';
import { CreateOpportunityComponent } from '../create-opportunity/create-opportunity.component';


interface Option {
  name: string;
  value: number;
}
interface Preference {
  name: string;
  code: string;
}

@Component({
  selector: 'create-client-profile-sidebar',
  standalone: true,
  imports: [
    SelectModule,
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    AvatarModule,
    ButtonModule,
    RippleModule,
    BlockUIModule,
    StyleClassModule,
    InputTextModule,
    InputMaskModule,
    DatePickerModule,
    MiniSearchComponent,
    ErrorMsgComponent,
    NgxFileDropModule,
    NgxIntlTelInputComponent,
    ToggleSwitchModule,
    DrawerModule,
    TranslateModule,
    AssignedToComponent,
    InputNumberModule,
    SkeletonModule,
    WalkInTourComponent,
    WalkInTrialComponent,
    CreateOpportunityComponent,
    LostDealComponent,
    MultiTenantListComponent,
    ToastModule,
    TextareaModule,
    AsyncPipe,
    NgClass
],
  templateUrl: './prospect-form.component.html',
  styleUrls: ['./prospect-form.component.scss'],
  providers: [ DialogService, MessageService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProspectFormComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly store = inject(Store);
  private readonly clientService = inject(ClientService);
  private readonly opportunityStageService = inject(OpportunityStageService);
  private readonly taskCategory = inject(TaskCategoryService);
  private readonly organizationService = inject(OrganizationService);
  private readonly datePipe = inject(DatePipe);
  readonly translate = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly authService = inject(AuthSevices);
  readonly commonService = inject(CommonService);
  private readonly omnisearchService = inject(OmniSearchService);
  private readonly toastMessageService = inject(ToastMessageService);
  private readonly dialogService = inject(DialogService);
  private readonly timeService = inject(TimeconversionService);
  private readonly messageService = inject(MessageService);
  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly advanceSettingService = inject(AdvancedSettingService);
  readonly locale = inject(LOCALE_ID);

  readonly showProspect = input(false);
  readonly clientData = input<ClientById | null>(null);
  readonly formType = input<'prospect' | 'walk-in-tour' | 'walk-in-trial' | 'walk-in-member' | 'contact'>('prospect');
  readonly formEvent = input<'create' | 'update'>('create');
  readonly searchData = input<{
    name?: string;
    email?: string;
    mobile?: string;
}>();
  readonly contactData = input<any>();
  readonly hideProspect = output<number>();
  readonly GoToClientDetails = output<{
    clientId: number;
}>();
  readonly proceedToSignup = output<{
    proceesToSignup: boolean;
    Data: any;
}>();
  readonly closeEditProfile = output<boolean>();

  // Properties for subscription management
  private readonly destroy$ = new Subject<void>();
  readonly isFormSubmitting = signal(false);
  readonly errorMessages = signal<string[]>([]);

  // Custom validator for ngx-intl-tel-input phone number
  // Standard Validators.required doesn't work with the object format
  private readonly phoneRequiredValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) return { required: true };
    
    // ngx-intl-tel-input outputs an object with 'number' property
    if (typeof value === 'object' && value !== null) {
      const phoneNumber = value.number ? String(value.number).replaceAll(/\s+/g, '').trim() : '';
      return phoneNumber.length >= 7 ? null : { required: true };
    }
    
    // If it's a string, check if it has content
    const strValue = String(value).replaceAll(/\s+/g, '').trim();
    return strValue.length >= 7 ? null : { required: true };
  };

  // Form properties
  fg: UntypedFormGroup = new UntypedFormGroup({});
  minDate: Date = new Date();
  readonly selectedTenant = signal(0);
  readonly state_code = signal('');
  readonly city_code = signal('');
  readonly country_code = signal('');
  readonly taskCategoryId = signal(0);
  readonly stageMasterId = signal(0);
  readonly showOtherSourceTextBox = signal(false);
  isCapitalizing = false;
  readonly modified = output<boolean>();

  // Phone number handling
  CountryISO = CountryISO;
  PhoneNumberFormat = PhoneNumberFormat;
  onlyCountries: CountryISO[] = [];
  tenantCountry!: string;
  phoneUtil: any = lpn.PhoneNumberUtil.getInstance();

  // Collections - ClientGender options from tenant settings (string array)
  readonly genderOptions = signal<string[]>([]);
  private static readonly DEFAULT_GENDER_OPTIONS: string[] = Object.entries(ClientGender)
    .filter(([, v]) => typeof v === 'number')
    .map(([key]) => key);
  channelCollection = CONSTANTS_DATA.CHANNEL_SOURCES;
  promotionalPreferenceCollection :any[]= CONSTANTS_DATA.PREFERENCE.filter(o => o.code != 'Phone');


  // State management
  readonly organizations = signal<Option[]>([]);
  readonly clientId = signal<number | null>(null);
  readonly isClientRepresentative = signal(true);
  claims: string[] = ['can_be_client_rep'];
  readonly selectedClientRep = signal(0);
  // BehaviorSubject version for AssignedToComponent that expects BehaviorSubject input
  readonly selectedClientRep$ = new BehaviorSubject<number>(0);
  readonly staffIds = signal<number[]>([0]);
  // Observable version of staffIds for components that expect Observable input
  readonly staffIds$ = toObservable(this.staffIds);
  readonly showClientOrganizationLink = signal(false);
  relationships = [
    { name: 'Parent', value: 11 },
    { name: 'Child', value: 12 },
    { name: 'Spouse', value: 13 },
    { name: 'Sibling', value: 14 }
  ];

  // Additional Information Properties
  readonly showAdditionalInformation = signal(false);
  selectedCountry: any;
  readonly countries = signal<any[]>([]);
  readonly nationalities = signal<Array<{ nationality: string; code: string }>>([]);
  readonly clientById = signal<any>(null);
  promotionPreferenceMap = new Map();
  existClientForEmail: any;
  existClientForMobile: any;
  readonly tenantDetails = signal<any>(null);
  readonly planSummarydataObject = signal<PlanSummary | null>(null);
  readonly showPaidFullSidebar = signal(false);
  userDetails: any;
  companyDetails :any;
  readonly referredBy = signal(0);
  readonly showSkeleton = signal(true);
  readonly supportedLanguages = signal<Array<LANGUAGE>>([]);
  refCropper?: DynamicDialogRef | null;
  readonly uploadClientImage = viewChild<ElementRef>('uploadClientImage');

  prospectData: any = {};
  readonly uploadSuccess = signal(false);
  uploadProfilePicture: boolean = false;
  imageBase64: string = '';
  existingImage: string = '';

  readonly serviceTypes = signal<any[]>([]);
  readonly showChooseReason = signal(false);
  readonly lostDealItemInfo = signal<{tenant_id:number,id:number,client_info:any} | null>(null);

  readonly emailAlreadyExists = signal(false);
  readonly mobileAlreadyExists = signal(false);
  readonly clientExistOnEmail = signal(false);
  readonly clientExistOnMobile = signal(false);

  readonly showClientSidebar = signal(false);
  readonly activeClientId = signal(0);
  clientMobileNoCountry = '';

  ngOnInit(): void {
    this.initializeForm();
    this.setupSubscriptions();
    
    // Load client data if editing existing client
    const clientData = this.clientData();
    if (clientData) {
      this.GetClient(clientData.id);
    } else {
      // For new prospects, setup promotional preferences and hide skeleton after initial data loads
      this.setupPromotionalPreferences(null);
    }
  }

  GetClient(clientId: number): void {
    this.clientService.getClientById(clientId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (client) => this.handleClientResponse(client),
        error: () => {
          this.showSkeleton.set(false);
          this.cdr.detectChanges();
        }
      });
  }

  handleClientResponse(client: any): void {
    this.clientById.set(client);
    this.referredBy.set(client.referred_by);
   
    if (client.mobile_country_code) {
      const countries = this.countries();
      const foundCountry = countries.find(o => 
        o.dial_code.replaceAll(/\D/g, "") === client.mobile_country_code.replaceAll(/\D/g, "")
      );
      if (foundCountry) {
        this.clientMobileNoCountry = foundCountry.two_letter_code.toLowerCase();
      }
    }
    this.updateFormWithClientData(client);
    this.showSkeleton.set(false);
    this.cdr.detectChanges();
  }

  // Update form with client data
  private updateFormWithClientData(client: ClientById): void {
    const genderValue = typeof client.gender === 'string'
      ? client.gender
      : client.gender != null
        ? (ClientGender[client.gender as ClientGender] ?? null)
        : null;
    const profileData = {
      full_name: client.full_name,
      mobileNo: client.mobile_number && client.mobile_number > 0 ? this.getParsedNumber(String(client.mobile_number)) : '',
      selectedGender: genderValue ?? this.genderOptions()[0] ?? '',
      emailId: client.email_id,
      channel: this.findChannel(client.channel),
      source: this.getSource(client),
      dateOfBirth: client.date_of_birth ? new Date(client.date_of_birth) : null,
      emergencyContact: client.emergency_contact_person,
      emergencyNumber: client.emergency_contact_number ? this.getParsedNumber(String(client.emergency_contact_number)) : null,
      medicalAlert: client.medical_alert,
      referedBy: client.referred_by,
      organisation: this.findOrganization(client.organisation),
      is_child: client.is_child,
      parent_client: client.parent_client_id,
      relation: this.relationships.find((c) => c.name == client.relation),
      promotionalPreference: this.getPromotionalPreferences(client),
      secondMobileNo: client.second_mobile_number ? this.getParsedNumber(String(client.second_mobile_number)) : null,
      preferredLanguage: client.preferred_language ? this.SetPrefersLanguage(client.preferred_language) : null,
      address_pin_code: client.address_pin_code,
      address: client.address,
      national_id: client.national_id,
      taxNumber: client.taxnumber,
      migrationId: client.migration_id,
      notes: client.note,
      newRepresentative: client.client_representative_id,
      referredBy: client.referred_by,
      nationality: (client as any).nationality || null
    };

    this.fg.patchValue(profileData);
    this.fg.updateValueAndValidity();
    this.selectedClientRep.set(client.client_representative_id);
    this.selectedClientRep$.next(client.client_representative_id);
    this.setupPromotionalPreferences(client);
    this.showOtherSourceTextBox.set(this.isOtherChannel(client.channel));
    this.NewRepresentative.disable();
    if(client.image && client.image != '' && client.image != null)
    {
      this.uploadSuccess.set(true);
      this.existingImage = client.image;
    }
  }

  private initializeForm(): void {
    this.fg = this.fb.group({
      full_name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]],
      emailId: [''],
      mobileNo: [''],
      channel: ['', [Validators.required]],
      source: [{ value: '', disabled: true }, [Validators.required]],
      selectedGender: ['', [Validators.required]],
      referredBy: [0],  
      promotionalPreference: [[]],
      createTask: [this.formType() === 'prospect'],
      createOpportunity: [false],
      createWalkInTour: [this.formType() === 'walk-in-tour'],
      createWalkInTrial: [this.formType() === 'walk-in-trial'],
      createWalkInMember: [this.formType() === 'walk-in-member'],
      taskType: new UntypedFormControl(null, []),
      taskOn: new UntypedFormControl([], []),
      taskOnDate: new UntypedFormControl('', []),
      interestedIn: new UntypedFormControl({ value: [] as Option[], disabled: true }, [Validators.required]), 
      newRepresentative: ['', [Validators.required]],
      organisation: [null],
      dateOfBirth: [null],
      emergencyContact: [''],
      emergencyNumber: [''],
      medicalAlert: [''],
      is_child: [false],
      parent_client: [''],
      relation: [null],
      secondMobileNo: [''],
      preferredLanguage: [null],
      address_pin_code: [''],
      address: [''],
      national_id: [''],
      taxNumber: [''],
      migrationId: [''],
      notes: [''],
      email:[false],
      whatsapp:[false],
      sms:[false],
      push:[false],
      image: [''],
      nationality: [null]
    });

    const formType = this.formType();
    if(formType === 'contact'){
      this.fg.addControl('tenant_id', new UntypedFormControl(null, [Validators.required]));
    }

    // Set search data if available
    const searchData = this.searchData();
    if (searchData && Object.keys(searchData).length > 0) {
      if (searchData.name?.trim()) {
        this.fg.patchValue({ full_name: searchData.name });
      }
      if (searchData.email?.trim()) {
        this.fg.patchValue({ emailId: searchData.email });
      }
      if (searchData.mobile?.trim()) {
        // Note: You might need to adjust this based on your phone input component's requirements
        if(formType === 'contact'){
          this.fg.patchValue({ mobileNo: this.getParsedNumber(String(searchData.mobile)) });
        }
        else{
          this.fg.patchValue({ mobileNo: searchData.mobile });
        }
      }
    }
    // Set initial values if needed
    if (!this.clientData()) {
      this.setDefaultValues();
    }

    // Update validity of all controls
    Object.values(this.fg.controls).forEach(control => {
      control.updateValueAndValidity({ emitEvent: false });
    });

    this.fg.setValidators(this.emailMobileValidator());
    this.fg.updateValueAndValidity({ emitEvent: false });
   
  }

  private emailMobileValidator(): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      const emailControl = formGroup.get('emailId');
      const mobileControl = formGroup.get('mobileNo');

      if (!emailControl || !mobileControl) {
        return null;
      }

      const email = emailControl.value;
      const mobile = mobileControl.value;

      // Check if at least one field is filled and valid
      if ((email && !emailControl.errors?.['email']) || 
          (mobile && !mobileControl.errors?.['phoneNumber'])) {
        return null;
      }

      return { emailOrMobileRequired: true };
    };
  }

  private setDefaultValues(): void {
    const options = this.genderOptions();
    let genderDefault: string | null = null;
    if (options.length === 1) {
      genderDefault = options[0];
    }
    const defaultValues = {
      selectedGender: genderDefault,
    };

    // Only set values that exist
    Object.entries(defaultValues).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        this.fg.get(key)?.setValue(value);
      }
    });
  }

  private setupFormValidation(): void {
    if (!this.fg) return; // Guard clause to prevent null access
    // Channel validation
    this.fg.get('channel')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((mode: any) => {
        if (mode?.channel) {
          this.fg.get('source')?.enable({ onlySelf: true });
          if (mode.channel.toLowerCase() === 'other') {
            this.fg.get('source')?.clearValidators();
            this.fg.get('source')?.setValue('');
            this.showOtherSourceTextBox.set(true);
          } else {
            this.fg.get('source')?.setValidators([Validators.required]);
            this.showOtherSourceTextBox.set(false);
          }
        } else {
          this.fg.get('source')?.clearValidators();
        }
        this.fg.get('source')?.updateValueAndValidity();
      });

      this.fg.get('is_child')?.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe((value: any) => {
        if (value) {
          this.fg.get('parent_client')?.setValidators(Validators.required);
          this.fg.get('parent_client')?.updateValueAndValidity({ onlySelf: true });
          this.fg.get('relation')?.setValidators(Validators.required);
          this.fg.get('relation')?.updateValueAndValidity({ onlySelf: true });
        } else {
          this.fg.get('parent_client')?.clearValidators();
          this.fg.get('parent_client')?.setValue('');
          this.fg.get('parent_client')?.updateValueAndValidity({ onlySelf: true });
          this.fg.get('relation')?.clearValidators();
          this.fg.get('relation')?.setValue(null);
          this.fg.get('relation')?.updateValueAndValidity({ onlySelf: true });
        }
        this.fg.updateValueAndValidity();
        this.cdr.detectChanges();
      });

    // Email/Mobile cross validation
    this.setupEmailMobileValidation();
  }

  private setupEmailMobileValidation(): void {
    // Email validation based on mobile
    this.fg.get('emailId')?.valueChanges
      .pipe(
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((value: any) => {
          if (value && this.fg.get('emailId')?.valid) {
          this.fg.get('mobileNo')?.removeValidators([this.phoneRequiredValidator]);
          if(this.mobileAlreadyExists()){
            this.MobileNoControl.setErrors({mobileExists:true});
          }
        } else {
          this.fg.get('mobileNo')?.setValidators([this.phoneRequiredValidator]);
          if(this.mobileAlreadyExists()){
            this.MobileNoControl.setErrors({mobileExists:true});
          }
        }
        this.fg.get('mobileNo')?.updateValueAndValidity({ onlySelf: true });
      });

    // Mobile validation based on email
    this.fg.get('mobileNo')?.valueChanges
      .pipe(
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((value: any) => {
        if (value && this.fg.get('mobileNo')?.valid) {
          this.fg.get('emailId')?.removeValidators([Validators.required, Validators.email]);
          if(this.emailAlreadyExists()){
            this.EmailIdControl.setErrors({emailExists:true});
          }
        } else {
          this.fg.get('emailId')?.setValidators([Validators.required, Validators.email]);
          if(this.emailAlreadyExists()){
            this.EmailIdControl.setErrors({emailExists:true});
          }
        }
        this.fg.get('emailId')?.updateValueAndValidity({ onlySelf: true });
      });
  }

  private updateSupportedLanguages(): void {
    // Safety check: return if companyDetails is not ready
    if (!this.companyDetails || !this.companyDetails.compDetails || !this.companyDetails.compDetails.supported_languages) {
      this.supportedLanguages.set([]);
      return;
    }
    
    let supportedLangBuffer: Array<LANGUAGE> = [];
    const supportedLang = this.companyDetails.compDetails.supported_languages.map((l: any) =>
      l.trim().toLowerCase()
    );
    supportedLangBuffer = CONSTANTS_DATA.ALL_LANGUAGES.filter((lang) => {
      const languageOnlower = lang.LANG_ABBRE.toLowerCase();
      const langCodeOnlower = lang.LANG_CODE.toLowerCase();
      if (supportedLang && supportedLang.length > 0) {
        return (
          supportedLang &&
          (supportedLang.includes(languageOnlower) ||
            supportedLang.includes(langCodeOnlower))
        );
      } else if (
        this.companyDetails.compDetails.primary_language == languageOnlower
      ) {
        return lang;
      } else {
        return { COUNTRY: 'us', LANG_CODE: 'en', LANG_ABBRE: 'ENGLISH' };
      }
    });
    this.supportedLanguages.set(supportedLangBuffer);
  }

  get parseSupportedLang(): Array<LANGUAGE> {
    return this.supportedLanguages();
  }

  private setupEmailMobileExistenceCheck(): void {
    // Check if email exists
    this.fg.get('emailId')?.valueChanges
      .pipe(
        debounceTime(1000),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
        switchMap(email => {
          const clientData = this.clientData();
          const isExistingClient = clientData ? clientData?.email_id == email : false;
          if (email && this.EmailIdControl.valid && !this.EmailIdControl.errors?.['email'] && !isExistingClient) {
            return this.checkClientExist(email);
          }
          return of(null);
        })
      )
      .subscribe(clients => {
        if (clients && clients.length > 0 && clients.find((c: { clt_emailid: any; }) => c.clt_emailid === this.EmailIdControl?.value)) {
          if(this.LinkedClientIdControl.value != 0 && this.LinkedClientIdControl.value !='' && this.LinkedClientIdControl.value == clients.find((c: { clt_emailid: any; }) => c.clt_emailid === this.EmailIdControl?.value).clt_id)
          {
            const currentErrors = this.EmailIdControl?.errors ? { ...this.EmailIdControl.errors } : {};
            delete currentErrors['emailExists'];
            this.EmailIdControl?.setErrors(Object.keys(currentErrors).length ? currentErrors : null);
            this.existClientForEmail = null;
            this.emailAlreadyExists.set(false);
            this.clientExistOnEmail.set(false);
            this.cdr.detectChanges();
          }
          else
          {
            // Set error and store existing client
            this.EmailIdControl?.setErrors({ emailExists: true });
            this.EmailIdControl?.updateValueAndValidity();
            this.EmailIdControl?.markAsTouched();
            this.existClientForEmail = clients.find((c: { clt_emailid: any; }) => c.clt_emailid === this.EmailIdControl?.value);
            this.emailAlreadyExists.set(true);
            this.clientExistOnEmail.set(true);
          }
          this.cdr.detectChanges();
        } else {
          // Clear emailExists error while preserving other errors
          const currentErrors = this.EmailIdControl?.errors ? { ...this.EmailIdControl.errors } : {};
          delete currentErrors['emailExists'];
          this.EmailIdControl?.setErrors(Object.keys(currentErrors).length ? currentErrors : null);
          this.EmailIdControl?.updateValueAndValidity();
          this.existClientForEmail = null;
          this.emailAlreadyExists.set(false);
          this.clientExistOnEmail.set(false);
          this.cdr.detectChanges();
        }
      });

    // Check if mobile number exists
    this.fg.get('mobileNo')?.valueChanges
      .pipe(
        debounceTime(1000),
        distinctUntilChanged((prev, curr) => {
          // Custom comparator for mobile number object
          const prevNum = prev ? (typeof prev === 'object' && prev.number ? String(prev.number).replaceAll(/\s+/g, '').trim() : String(prev).replaceAll(/\s+/g, '').trim()) : '';
          const currNum = curr ? (typeof curr === 'object' && curr.number ? String(curr.number).replaceAll(/\s+/g, '').trim() : String(curr).replaceAll(/\s+/g, '').trim()) : '';
          return prevNum === currNum;
        }),
        takeUntil(this.destroy$),
        switchMap(mobileNo => {
          // Handle both new object format and string format
          const parsedNumber = mobileNo ? 
            (typeof mobileNo === 'object' && mobileNo.number ? 
              String(mobileNo.number).replaceAll(/\s+/g, '').trim() : 
              String(mobileNo).replaceAll(/\s+/g, '').trim()) : '';
          
          const clientData = this.clientData();
          const isExistingClient = clientData ? 
            (clientData?.mobile_number?.toString() == parsedNumber) : false;
          
          
          // Check parsed number length instead of Angular form control validity
          // This avoids race condition where .valid hasn't updated yet
          if (parsedNumber && parsedNumber.length >= 7 && !isExistingClient) {
            return this.checkClientExist(parsedNumber);
          }
          return of(null);
        })
      )
      .subscribe(clients => {
        const mobileControl = this.fg.get('mobileNo');
        // Handle both new object format and string format
        const parsedNumber = mobileControl?.value ? 
          (typeof mobileControl.value === 'object' && mobileControl.value.number ? 
            String(mobileControl.value.number).replaceAll(/\s+/g, '').trim() : 
            String(mobileControl.value).replaceAll(/\s+/g, '').trim()) : '';
          
        if (clients && clients.length > 0 && clients.find((c: { clt_mobile_no: any; }) => c.clt_mobile_no.toString() === parsedNumber.toString())) {
          if(this.LinkedClientIdControl.value != 0 && this.LinkedClientIdControl.value !='' && this.LinkedClientIdControl.value == clients.find((c: { clt_mobile_no: any; }) => c.clt_mobile_no.toString() === parsedNumber.toString()).clt_id)
          {
            // Clear mobileExists error while preserving other errors
            const currentErrors = this.MobileNoControl?.errors ? { ...this.MobileNoControl.errors } : {};
            delete currentErrors['mobileExists'];
            this.MobileNoControl?.setErrors(Object.keys(currentErrors).length ? currentErrors : null);
            this.existClientForMobile = null;
            this.mobileAlreadyExists.set(false);
            this.clientExistOnMobile.set(false);
          }
          else{
            // Set error and store existing client
            this.MobileNoControl?.setErrors({ mobileExists: true });
            this.MobileNoControl?.markAsTouched();
            this.existClientForMobile = clients.find((c: { clt_mobile_no: any; }) => c.clt_mobile_no.toString() === parsedNumber.toString());
            this.mobileAlreadyExists.set(true);
            this.clientExistOnMobile.set(true);
          }
        } else {
          // Clear mobileExists error while preserving other errors
          const currentErrors = this.MobileNoControl?.errors ? { ...this.MobileNoControl.errors } : {};
          delete currentErrors['mobileExists'];
          this.MobileNoControl?.setErrors(Object.keys(currentErrors).length ? currentErrors : null);
          this.existClientForMobile = null;
          this.mobileAlreadyExists.set(false);
          this.clientExistOnMobile.set(false);
        }
        this.cdr.detectChanges();
      });

  }

  private checkClientExist(query: string): Observable<any> {
    if (!query) {
      return of(null);
    }
    
    const request = {
      language: this.locale,
      query: query,
      providers: [
        {
          provider_name: ElasticProviderName.Clients,
          pagination: { page_number: 0, page_size: 10 },
        }
  ],
    };

    return this.omnisearchService.Search(request).pipe(
      map(response => response.searchResults.clients.items),
      catchError(() => of(null))
    );
  }

  
  formFieldChanges()
  {
    // Add form control value change subscriptions
    this.fg.get('channel')?.valueChanges.subscribe(
      (mode: any) => {
          if (mode?.channel) {
          this.fg.get('source')?.setValue('');
          if (mode.channel.toLowerCase() === 'other') {
            this.fg.get('source')?.clearValidators();
            this.showOtherSourceTextBox.set(true);
          }
          else {
            this.fg.get('source')?.setValidators([Validators.required]);
            this.showOtherSourceTextBox.set(false);
          }
        } else {
          this.fg.get('source')?.clearValidators();
        }
        this.fg.get('source')?.updateValueAndValidity();
      }
    );

    // Note: Email/mobile validator switching is already handled in setupFormValidation()
    // This duplicate logic has been removed to avoid conflicts

   
  }

  private setupSubscriptions(): void {
    // Company info subscription
    this.store.select(COMPANY_INFO)
      .pipe(takeUntil(this.destroy$))
      .subscribe(responseData => {
        this.companyDetails = responseData;
        this.translate.setDefaultLang(responseData?.selectedLang?.LANG_CODE);
        this.translate.use(responseData?.selectedLang?.LANG_CODE);
        // Update supported languages signal
        this.updateSupportedLanguages();
      });

    // Tenant info subscription
    this.store.select(TENANTS_INFO)
      .pipe(takeUntil(this.destroy$))
      .subscribe(tenant => {
        this.selectedTenant.set(tenant.selected_tenant.id);
        this.tenantDetails.set(tenant.selected_tenant);
        this.country_code.set(tenant.selected_tenant.country);
        this.state_code.set(tenant.selected_tenant.state);
        this.city_code.set(tenant.selected_tenant.city);
        this.loadInitialData(tenant.selected_tenant);
      });

      this.store.select(STAFF_STATE).pipe(takeUntil(this.destroy$))
        .subscribe((data: STATE_STAFF_LIST) => {
          if (data?.staffList?.length > 0) {
            this.userDetails = data.staffList.find(
            (o: { id: number }) => o.id == this.authService.loggedInStaffId
          );
          const staffIds: number[] = [];
          data.staffList.forEach(s => {
            if (s.role == Roles.Admin || s.role.toString() == Roles[Roles.Admin]) {
              staffIds.push(s.id)
            }
            else if (s.role == Roles.Staff || s.role.toString() == Roles[Roles.Staff]) {
              if (s.can_be_client_rep) staffIds.push(s.id);
            }
          });
          this.staffIds.set(staffIds);
          if (this.userDetails?.can_be_client_rep) {
            this.NewRepresentative.setValue(this.userDetails.id);
            this.selectedClientRep.set(this.userDetails.id);
            this.selectedClientRep$.next(this.userDetails.id);
          }
        }
      });

      this.featureFlagService.getFlag$('client_organisation_link')
        .pipe(takeUntil(this.destroy$))
        .subscribe((clientOrgLink) => {
          this.showClientOrganizationLink.set(clientOrgLink);
        });

      this.store.select(SERVICETYPE_LIST_STATE).pipe(takeUntil(this.destroy$)).subscribe({
        next: (serviceTypes: any) => {
          this.serviceTypes.set(serviceTypes);
        }
      });
    // Form control subscriptions
    this.setupFormValidation();
    this.setupEmailMobileExistenceCheck();
  }

  private loadInitialData(tenant: any): void {
    const tenantId = tenant.id;
    this.selectedTenant.set(tenantId);

    this.loadClientGenderOptions(tenantId);
    
    // Load data directly instead of using resource API
    forkJoin({
      organizations: this.organizationService.ListOrganization(tenantId),
      taskCategories: this.taskCategory.getTaskCategory(tenantId),
      opportunityStages: this.opportunityStageService.OpportunityStageMasterListService(
        tenantId, 
        OpportunityBoardType.NewMemberBoard
      )
    }).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (data) => {
        this.handleOrganizations(data.organizations);
        this.handleTaskCategories(data.taskCategories);
        this.handleOpportunityStages(data.opportunityStages);
        if (!this.clientData()) {
          this.setupPromotionalPreferences(null);
        }
        this.showSkeleton.set(false);
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error loading initial data:', error);
        this.showSkeleton.set(false);
        this.cdr.detectChanges();
      }
    });
    
    // Handle country data separately as it comes from store
    this.GetCountry(tenant.country);
  }

  private loadClientGenderOptions(tenantId: number): void {
    this.advanceSettingService.getAdvanceSetting(tenantId, 'gender_allowed')
      .pipe(takeUntil(this.destroy$), catchError(() => of(null)))
      .subscribe({
        next: (setting) => {
          const options = ProspectFormComponent.parseGenderOptions(setting?.value);
          this.genderOptions.set(options);
          if (options.length === 1) {
            this.fg.get('selectedGender')?.setValue(options[0]);
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.genderOptions.set(ProspectFormComponent.DEFAULT_GENDER_OPTIONS);
          this.cdr.detectChanges();
        }
      });
  }

  private static parseGenderOptions(value: string | null | undefined): string[] {
    if (!value?.trim()) {
      return ProspectFormComponent.DEFAULT_GENDER_OPTIONS;
    }
    const mapped = value.split(',')
      .map(v => ClientGender[Number(v.trim()) as ClientGender])
      .filter((name): name is string => !!name);
    return mapped.length > 0 ? mapped : ProspectFormComponent.DEFAULT_GENDER_OPTIONS;
  }

  valueChangeFunction() {
    this.EmailIdControl.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe((value: any) => {
        if (value && this.EmailIdControl.valid) {
          this.MobileNoControl.clearValidators();
        } else {
          this.MobileNoControl.setValidators([this.phoneRequiredValidator]);
        }
        this.MobileNoControl.updateValueAndValidity({ onlySelf: true });
      });

    this.MobileNoControl.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe(() => {
        // Check if mobile has a valid number using the same logic as phoneRequiredValidator
        const mobileValue = this.MobileNoControl.value;
        const hasValidMobile = mobileValue && 
          (typeof mobileValue === 'object' ? 
            String(mobileValue.number || '').replaceAll(/\s+/g, '').trim().length >= 7 :
            String(mobileValue).replaceAll(/\s+/g, '').trim().length >= 7);
        
        if (hasValidMobile) {
          this.EmailIdControl.clearValidators();
        } else {
          this.EmailIdControl.setValidators([Validators.required, Validators.email]);
        }
        this.EmailIdControl.updateValueAndValidity({ onlySelf: true });
      });
  }

  GetOrganizations(tenantId: number): void {
    this.organizationService.ListOrganization(tenantId).subscribe({
      next: (organizations: any) => {
        if (organizations.items) {
          this.organizations.set(organizations.items.map((organization: any) => ({
            name: organization.name_primary_language,
            value: organization.id
          })));
        }
      },
      error: (error: any) => {
        this.toastMessageService.showError(error);
      }
    });
  }

  getTaskCategory(tenantId: number): void {
    this.taskCategory.getTaskCategory(tenantId).subscribe({
      next: (taskCategories: any) => {
        if (taskCategories?.items?.length > 0) {
          const salesCategory = taskCategories.items.find(
            (obj: any) => String(obj.name_primary_language).toLowerCase() === 'sales'
          );
          if (salesCategory) {
            this.taskCategoryId.set(salesCategory.id);
            this.modified.emit(true);
          }
        }
      },
      error: (error: any) => {
        this.toastMessageService.showError(error);
      }
    });
  }

  GetOpportunityStageMaster(tenantId: number): void {
    this.opportunityStageService
      .OpportunityStageMasterListService(tenantId, OpportunityBoardType.NewMemberBoard)
      .subscribe({
        next: (obj: any) => {
          if (obj?.items?.length > 0) {
            const leadOpps = obj.items.find(
              (o: any) => o.stage_master_key === OpportunityStageForNonMember.NewLeads
            );
            if (leadOpps?.id > 0) {
              this.stageMasterId.set(leadOpps.id);
            }
          }
        },
        error: (error: any) => {
          this.toastMessageService.showError(error);
        }
      });
  }

  GetCountry(countryCode: string): void {
    this.store.select(COUNTRY_MASTER_STATE_LIST)
      .pipe(takeUntil(this.destroy$))
      .subscribe((country) => {
        if (country.length > 0) {
          this.countries.set(country);
          
          // Populate nationalities from country store
          const nationalitiesList = country
            .map(c => ({ nationality: c.nationality, code: c.two_letter_code }))
            .sort((a, b) => a.nationality.localeCompare(b.nationality));
          this.nationalities.set(nationalitiesList.filter(n => n.nationality !== null && n.nationality !== ''));
          
          const foundCountry = country.find(o => 
            o.three_letter_code.toLowerCase() === countryCode.toLowerCase()
          );
          if (foundCountry) {
            this.tenantCountry = foundCountry.two_letter_code.toLowerCase();
            this.onlyCountries = Object.values(CountryISO);
          }
          const clientById = this.clientById();
          if (clientById?.mobile_country_code) {
            const clientCountry = country.find(o => 
              o.dial_code.replaceAll(/\D/g, "") === clientById.mobile_country_code.replaceAll(/\D/g, "")
            );
            if (clientCountry) {
              this.clientMobileNoCountry = clientCountry.two_letter_code.toLowerCase();
            }
          }
          const searchData = this.searchData();
          if (searchData?.mobile) {
            try {
              const phoneUtil = lpn.PhoneNumberUtil.getInstance();
              const parsedNumber = phoneUtil.parse(searchData.mobile);
              const countryCode = parsedNumber.getCountryCode();
              if (countryCode) {
                const searchCountry = country.find(o => 
                  o.dial_code.replaceAll(/\D/g, "") === countryCode.toString()
                );
                if (searchCountry) {
                  this.clientMobileNoCountry = searchCountry.two_letter_code.toLowerCase();
                }
              }
            } catch {
              // Phone number parsing failed, ignore
            }
          }
          this.cdr.detectChanges();
        } else {
          this.countries.set([]);
          this.nationalities.set([]);
        }
      });
  }

  SetGender(gender: string): void {
    this.fg.get('selectedGender')?.setValue(gender);
  }

  SetGenderOnChange($event: { value: string }): void {
    this.SetGender($event.value);
  }

  GetSelectedGenderFromForm(): string {
    const value = this.fg.get('selectedGender')?.value;
    return typeof value === 'string' ? value : (value?.value ?? ClientGender[ClientGender.PreferNotToRespond]);
  }

  getPromotionalPreferenceControlName(option: { name: string; code: string }): string {
    switch (option.code) {
      case 'Email':
        return 'email';
      case 'SMS':
        return 'sms';
      case 'WhatsApp':
        return 'whatsapp';
      case 'Push':
        return 'push';
      default:
        return option.name.toLowerCase();
    }
  }

  PromotionalPreferenceToTrue(notificationChannel: any): void {
    if (this.promotionPreferenceMap.has(notificationChannel.name)) {
      this.promotionPreferenceMap.delete(notificationChannel.name);
    } else {
      this.promotionPreferenceMap.set(notificationChannel.name, notificationChannel);
    }
    this.PromotionalPreferenceControl.setValue(Array.from(this.promotionPreferenceMap.values()).map((o: { code: any; }) => o.code));
  }



  IsPromotionOptionSelected(value: Preference): boolean {
    const formValue = this.fg.get('promotionalPreference')?.value;
    if (Array.isArray(formValue) && typeof formValue[0] === 'string') {
      return formValue.includes(value.code);
    }
    return formValue?.some((o: Preference) => o.name === value.name);
  }

  capitalizeInput() {
    if (this.isCapitalizing) return;
    this.isCapitalizing = true;
    const nameControl = this.NameControl;
    if (nameControl.value) {
      const capitalizedValue = nameControl.value.charAt(0).toUpperCase() + nameControl.value.slice(1);
      nameControl.setValue(capitalizedValue, { emitEvent: false });
    }
    this.isCapitalizing = false;
  }

  CreateProspectSubmit() {
    if (this.fg.valid) {
      this.isFormSubmitting.set(true);
      
      let request$: Observable<any>;
        if(this.clientData()){
          request$ = this.UpdateClient(this.SetClientUpdateObject());
        }else{
          request$ = this.CreateProspect(this.SetProspectObject());
        }
     
      request$
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => this.isFormSubmitting.set(false))
        )
        .subscribe({
          next: (response: any) => this.handleSubmitSuccess(response),
          error: (error) => {
            this.handleError(error);
            this.isFormSubmitting.set(false);
          }
        });
    } else {
      this.markFormGroupTouched(this.fg);
      this.cdr.detectChanges();
    }
  }

  private UpdateClient(payload: any): Observable<any> {
    const clientById = this.clientById();
    const clientDataObject = this.GetClientPatchObject(payload, clientById);
    if(clientDataObject && clientDataObject.length > 0)
    {
      return this.clientService.patchClient(clientById.id, this.selectedTenant(), clientDataObject)

      .pipe(
        tap(() => {
          this.translate
            .get('EDIT_CLIENT_PROFILE.MSG_UPDATE')
            .subscribe((translation) => {
              this.toastMessageService.showSuccess(translation);
            });
        }),
        delay(500)
      );
    }
    else
    {
      // Show info message when there are no changes to update
      this.translate
        .get('EDIT_CLIENT_PROFILE.MSG_NO_CHANGES')
        .subscribe((translation) => {
          this.toastMessageService.showInfo(translation || 'Nothing to update');
        });
      return of(null);
    }
  }

  private CreateProspect(payload: CreateProspectRequest): Observable<any> {
    let service: any;
    if(this.formType() === 'contact')
    {
      service = this.clientService.createProspectFromContact(this.TenantIdControl.value,this.contactData().contact_id,payload);
    }
    else{
      service = this.clientService.createProspect(payload);
    }
    return service.pipe(
        tap((response: CreateProspectResponse) => {
          this.clientId.set(response.client_id);
        this.commonService.addProspect(true);
        this.translate
          .get('Shared.CREATE-PROSPECT.CLIENT_SUCCESS')
          .subscribe((translation) => {
           const data = {
          clientName: this.NameControl.value,
          clientId: response.client_id,
        };
        this.messageService.add({severity:'success', key: 'client-toast', summary: 'Success', detail: translation, data: data});
        });
        this.cdr.detectChanges();
      }),
        delay(1000)
      );
  }

  private async handleSubmitSuccess(response: any) {
    this.clientId.set(response.client_id ? response.client_id : response.id ? response.id : 0);
    const formType = this.formType();
    const formEvent = this.formEvent();
    if(formEvent == 'create' && formType == 'prospect'){
      this.commonService.addProspect(true);
      await this.timeout(4000);
      this.isFormSubmitting.set(false);
      this.closeSidebar();
    }
    else if(formEvent == 'create' && formType == 'walk-in-member'){
      this.isFormSubmitting.set(false);
      this.proceedToSignup.emit({proceesToSignup:true,Data:this.setSignupData(response)});
      this.closeSidebar();
    }
    else if(formEvent == 'create' && formType == 'walk-in-tour'){
      if(this.fg.get('tour_completed')?.value?.tour_outcome.shortname === 'signup'){
        this.isFormSubmitting.set(false);
        this.commonService.addProspect(true);
        this.proceedToSignup.emit({proceesToSignup:true,Data:this.setSignupData(response)});
        this.closeSidebar();
      }
      else if(this.fg.get('tour_completed')?.value?.tour_outcome.shortname === 'notintrested'){
        this.isFormSubmitting.set(false);
        this.lostDealItemInfo.set({tenant_id:this.selectedTenant(),id:response.opportunity_id,client_info:{id:response.client_id,full_name:this.NameControl.value,status:'Prospect',image:'https://www.google.com/images/branding/googlelogo/1x/googlelogo_color_272x92dp.png'}});
        this.showChooseReason.set(true);
        this.commonService.addProspect(true);
      }
    }
    else if(formEvent == 'update'){
      this.isFormSubmitting.set(false);
      this.closeSidebar(true);
    }
    else if(formEvent == 'create' && formType == 'contact'){
      this.isFormSubmitting.set(false);
      this.closeSidebar(true);
    }
  }

  setSignupData(response: CreateProspectResponse){
    const planSummary: PlanSummary = {
      id: 0,
      from_subscription_id:0,
      client: {
        client_id: response.client_id,
        client_name: this.NameControl.value,
        client_status: 'Prospect',
      } as PlanSummaryClient,
      couponcode: null,
      tenant_id: this.selectedTenant(),
      company_id: this.tenantDetails()?.company_id,
      startdate: new Date(),
      plan_selectable: true,
      CRM: {
        opp_id: 0,
        task_id: response.task_id,
        board_id: OpportunityBoardType.NewMemberBoard,
      } as PlanSummaryCRM,
      Scheduling: null,
    } as PlanSummary;
    return planSummary;
  }

  private handleError(error: any) {
    this.toastMessageService.showError(error);
  }


  private handleOrganizations(response: any): void {
    if (response?.items) {
      this.organizations.set(response.items.map((organization: any) => ({
        name: organization.name_primary_language,
        value: organization.id
      })));
    }
  }

  private handleTaskCategories(response: any): void {
    if (response?.items?.length > 0) {
      const salesCategory = response.items.find(
        (obj: any) => String(obj.name_primary_language).toLowerCase() === 'sales'
      );
      if (salesCategory) {
        this.taskCategoryId.set(salesCategory.id);
      }
    }
  }

  private handleOpportunityStages(response: any): void {
    if (response?.items?.length > 0) {
      const leadOpps = response.items.find(
        (o: any) => o.stage_master_key === OpportunityStageForNonMember.NewLeads
      );
      if (leadOpps?.id > 0) {
        this.stageMasterId.set(leadOpps.id);
      }
    }
  }

  GetClientPatchObject(updateProfileData: any, clientdata: any): Array<{ op: string, path: string, value: any }> {
    const clientObj: Array<{ op: string, path: string, value: any }> = [];
    const mobileNumberChanged = this.isValueChanged(['mobile_number', updateProfileData.mobile_number], clientdata);
    const mobileCountryCodeChanged = this.isValueChanged(['mobile_country_code', updateProfileData.mobile_country_code], clientdata);

    Object.entries(updateProfileData).forEach(obj => {
      if (obj[0] === 'mobile_number' || obj[0] === 'mobile_country_code') {
        return;
      }

      const isChanged = this.isValueChanged(obj, clientdata);
      
      if (isChanged) {
        switch (obj[0]) {
          case 'full_name':
            clientObj.push({ op: "replace", path: "/FullName", value: obj[1] });
            break;
          case 'date_of_birth':
            clientObj.push({ op: "replace", path: "/DateOfBirth", value: obj[1] });
            break;
          case 'gender':
            clientObj.push({ op: "replace", path: "/Gender", value: obj[1] });
            break;
          case 'email_id':
            clientObj.push({ op: "replace", path: "/EmailId", value: obj[1] });
            break;
          case 'source':
            clientObj.push({ op: "replace", path: "/Source", value: obj[1] });
            break;
          case 'channel':
            clientObj.push({ op: "replace", path: "/Channel", value: obj[1] });
            break;
          case 'emergency_contact_number':
            clientObj.push({ op: "replace", path: "/EmergencyContactNumber", value: obj[1] });
            break;
          case 'emergency_contact_person':
            clientObj.push({ op: "replace", path: "/EmergencyContactPerson", value: obj[1] });
            break;
          case 'medical_alert':
            clientObj.push({ op: "replace", path: "/MedicalAlert", value: obj[1] });
            break;
          case 'organisation':
            clientObj.push({ op: "replace", path: "/OrganisationId", value: obj[1] });
            break;
          case 'promotional_notification_channels_allowed':
            clientObj.push({ op: "replace", path: "/PromotionalNotificationChannelsAllowed", value: obj[1] && (obj[1] as string[]).length > 0 ? obj[1] : ['None'] });
            break;
          case "is_child":
            clientObj.push({ op: "replace", path: "/IsChild", value: obj[1] });
            break;
          case "parent_client_id":
            clientObj.push({ op: "replace", path: "/ParentClientId", value: obj[1] });
            break;
          case "relation":
            clientObj.push({ op: "replace", path: "/Relation", value: obj[1] });
            break;
          case "image":
            clientObj.push({ op: "replace", path: "/ImageBase64", value: obj[1] });
            break;
          case "taxnumber":
            clientObj.push({ op: "replace", path: "/TaxNumber", value: obj[1] });
            break;
          case "migration_id":
            clientObj.push({ op: "replace", path: "/MigrationId", value: obj[1] });
            break;
          case "note":
            clientObj.push({ op: "replace", path: "/Note", value: obj[1] });
            break;
          case "national_id":
            clientObj.push({ op: "replace", path: "/NationalId", value: obj[1] });
            break;
          case "medical_alert":
            clientObj.push({ op: "replace", path: "/MedicalAlert", value: obj[1] });
            break;
          case "address":
            clientObj.push({ op: "replace", path: "/Address", value: obj[1] });
            break;
          case "address_pin_code":
            clientObj.push({ op: "replace", path: "/AddressPinCode", value: obj[1] });
            break;
          case "place_id_request":
            clientObj.push({ op: "replace", path: "/PlaceIdRequest", value: obj[1] });
            break;
          case "preferred_language":
            clientObj.push({ op: "replace", path: "/PreferredLanguage", value: obj[1] });
            break;
          case "nationality":
            clientObj.push({ op: "replace", path: "/Nationality", value: obj[1] });
            break;
          default:
            break;
        }
      }
    });

    if (mobileNumberChanged || mobileCountryCodeChanged) {
      clientObj.push({ op: "replace", path: "/MobileNumber", value: updateProfileData.mobile_number });
      clientObj.push({ op: "replace", path: "/MobileCountryCode", value: updateProfileData.mobile_country_code });
    }

    return clientObj;
  }

  isValueChanged(obj: any, clientdata: any) {
    const fieldName = obj[0];
    const newValue = obj[1];
    
    const found = Object.entries(clientdata)
      .find(pair => pair[0] === fieldName);

    if (!found) {
      // Field doesn't exist in original data, consider it changed only if it has a meaningful value
      return this.hasMeaningfulValue(newValue);
    }

    const originalValue = found[1];
    
    // Use deepEqual for comprehensive comparison
    return !this.deepEqual(newValue, originalValue, fieldName);
  }

  /**
   * Deep equality check with special handling for different field types
   * Similar to the tenant component's deepEqual method
   */
  private deepEqual(obj1: any, obj2: any, key: string): boolean {
    // Exact match
    if (obj1 === obj2) {
      return true;
    }

    // Handle null/undefined/empty string as equivalent for most fields
    if (this.isEmptyValue(obj1) && this.isEmptyValue(obj2)) {
      return true;
    }

    // Special handling for different field types
    switch (key) {
      case 'mobile_number':
      case 'second_mobile_number':
        return this.compareMobileNumbers(obj1, obj2);
      
      case 'mobile_country_code':
      case 'second_mobile_country_code':
        return this.compareDialCodes(obj1, obj2);
      
      case 'email_id':
        return this.compareEmails(obj1, obj2);
      
      case 'organisation':
        return this.isOrganisationChanged(obj2, obj1) === false; // Inverted because isOrganisationChanged returns true when changed
      
      case 'promotional_notification_channels_allowed':
        return this.compareArrays(obj1, obj2);
      
      case 'date_of_birth':
        return this.compareDates(obj1, obj2);
      
      case 'gender': {
        const norm1 = typeof obj1 === 'string' ? obj1 : (obj1 != null && typeof obj1 === 'number' ? ClientGender[obj1 as ClientGender] : obj1);
        const norm2 = typeof obj2 === 'string' ? obj2 : (obj2 != null && typeof obj2 === 'number' ? ClientGender[obj2 as ClientGender] : obj2);
        return this.compareStrings(norm1, norm2);
      }
      case 'channel':
      case 'source':
      case 'relation':
        return this.compareStrings(obj1, obj2);
      
      case 'is_child':
        return this.compareBooleans(obj1, obj2);
      
      case 'image':
        return this.compareImages(obj1, obj2);
      
      case 'emergency_contact_number':
        return this.compareE164Numbers(obj1, obj2);
      
      default: {
        // Default comparison using normalized values
        const normalizedObj1 = this.normalizeValue(obj1);
        const normalizedObj2 = this.normalizeValue(obj2);
        return normalizedObj1 === normalizedObj2;
      }
    }
  }

  private isEmptyValue(value: any): boolean {
    return value === null || value === undefined || value === '' || value === 0;
  }

  private compareMobileNumbers(newVal: any, originalVal: any): boolean {
    // Normalize both values by removing spaces, dashes, parentheses
    const normalizePhone = (val: any): string => {
      if (!val) return '';
      return String(val).replaceAll(/[\s\-()]/g, '').trim();
    };

    return normalizePhone(newVal) === normalizePhone(originalVal);
  }

  private compareDialCodes(newVal: any, originalVal: any): boolean {
    // Normalize dial codes by removing + and spaces
    const normalizeDialCode = (val: any): string => {
      if (!val) return '';
      return String(val).replaceAll(/[\s+]/g, '').trim();
    };

    return normalizeDialCode(newVal) === normalizeDialCode(originalVal);
  }

  private compareEmails(newVal: any, originalVal: any): boolean {
    if (!newVal && !originalVal) return true;
    if (!newVal || !originalVal) return false;
    return String(newVal).toLowerCase().trim() === String(originalVal).toLowerCase().trim();
  }

  private compareArrays(newVal: any, originalVal: any): boolean {
    if (!Array.isArray(newVal) && !Array.isArray(originalVal)) {
      return true;
    }
    if (!Array.isArray(newVal) || !Array.isArray(originalVal)) {
      return false;
    }
    
    // Sort and compare
    const sortedNew = [...newVal].sort();
    const sortedOriginal = [...originalVal].sort();
    
    return JSON.stringify(sortedNew) === JSON.stringify(sortedOriginal);
  }

  private compareDates(newVal: any, originalVal: any): boolean {
    if (!newVal && !originalVal) return true;
    if (!newVal || !originalVal) return false;
    
    // Compare date strings (format: yyyy-MM-dd)
    const normalizeDate = (val: any): string => {
      if (!val) return '';
      // If it's already a formatted string, return it
      if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
        return val;
      }
      // Otherwise try to parse and format
      try {
        const date = new Date(val);
        return this.datePipe.transform(date, 'yyyy-MM-dd') || '';
      } catch {
        return '';
      }
    };

    return normalizeDate(newVal) === normalizeDate(originalVal);
  }

  private compareStrings(newVal: any, originalVal: any): boolean {
    if (!newVal && !originalVal) return true;
    if (!newVal || !originalVal) return false;
    return String(newVal).toLowerCase().trim() === String(originalVal).toLowerCase().trim();
  }

  private compareBooleans(newVal: any, originalVal: any): boolean {
    return Boolean(newVal) === Boolean(originalVal);
  }

  private compareImages(newVal: any, originalVal: any): boolean {
    // If both are empty, they're equal
    if (!newVal && !originalVal) return true;
    
    // If one is empty and other is not, they're different
    if (!newVal || !originalVal) return false;
    
    // Remove base64 prefix if present for comparison
    const normalizeImage = (val: any): string => {
      if (!val) return '';
      return String(val).replaceAll('data:image/png;base64,', '').trim();
    };

    return normalizeImage(newVal) === normalizeImage(originalVal);
  }

  private compareE164Numbers(newVal: any, originalVal: any): boolean {
    if (!newVal && !originalVal) return true;
    if (!newVal || !originalVal) return false;
    
    // Normalize E164 numbers
    const normalizeE164 = (val: any): string => {
      if (!val) return '';
      return String(val).replaceAll(/[\s\-()]/g, '').trim();
    };

    return normalizeE164(newVal) === normalizeE164(originalVal);
  }

  private normalizeValue(value: any): any {
    // Handle null, undefined, empty string as equivalent
    if (value === null || value === undefined || value === '') {
      return null;
    }
    
    // Handle zero values
    if (value === 0 || value === '0') {
      return 0;
    }
    
    // Convert numbers to strings for consistent comparison
    if (typeof value === 'number') {
      return value.toString();
    }
    
    // Return as-is for other types
    return value;
  }

  private hasMeaningfulValue(value: any): boolean {
    return value !== null && value !== undefined && value !== '' && value !== 0;
  }

  private isOrganisationChanged(originalValue: any, newValue: any): boolean {
    // Both are empty/null - no change
    if (!this.hasMeaningfulValue(originalValue) && !this.hasMeaningfulValue(newValue)) {
      return false;
    }
    
    // One is empty, other has value - it's a change
    if (!this.hasMeaningfulValue(originalValue) !== !this.hasMeaningfulValue(newValue)) {
      return true;
    }
    
    // Both have values - compare IDs
    const originalId = originalValue?.id || originalValue;
    const newId = newValue?.id || newValue;
    
    return originalId !== newId;
  }

  timeout(ms: any) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private getParsedNumber(phoneNumber: string): any {
    if (!phoneNumber) return '';
    
    let number: lpn.PhoneNumber = '' as unknown as lpn.PhoneNumber;
    let num: any = 0;
    try {
      // Get phone util instance
      const phoneUtil = lpn.PhoneNumberUtil.getInstance();
      
      // Parse number differently if it already has a plus sign (has dial code)
      if (phoneNumber.startsWith('+')) {
        number = phoneUtil.parse(phoneNumber);
      } else {
        number = phoneUtil.parse(phoneNumber,this.clientMobileNoCountry ? this.clientMobileNoCountry.toUpperCase() : this.tenantCountry.toUpperCase());
      }
      
      num = phoneUtil.format(number, lpn.PhoneNumberFormat.NATIONAL);
    } 
    catch (e) {
      console.error("Error parsing phone number:", e);
    }
    return num;
  }

  SetClientUpdateObject() {
    const baseData = {
      full_name: this.fg.get('full_name')?.value,
      mobile_country_code: this.fg.get('mobileNo')?.value?.dialCode || '',
      mobile_number: this.fg.get('mobileNo')?.value?.number?.replaceAll(/[\s\-()]/g, '') || 0,
      email_id: this.fg.get('emailId')?.value,
      date_of_birth: this.fg.get('dateOfBirth')?.value ? this.datePipe.transform(this.fg.get('dateOfBirth')?.value, 'yyyy-MM-dd') : null,
      gender: this.GetSelectedGenderFromForm(),
      second_mobile_country_code: this.fg.get('secondMobileNo')?.value?.dialCode || null,
      second_mobile_number: this.fg.get('secondMobileNo')?.value?.number?.replaceAll(/[\s\-()]/g, '') || null,
      preferred_language: this.fg.get('preferredLanguage')?.value?.LANG_CODE || '',
      address: this.fg.get('address')?.value,
      address_pin_code: this.fg.get('address_pin_code')?.value,
      emergency_contact_number: this.fg.get('emergencyNumber')?.value?.e164Number || null,
      emergency_contact_person: this.fg.get('emergencyContact')?.value,
      source: this.fg.get('source')?.value || 'Other',
      channel: this.fg.get('channel')?.value?.channel,
      referred_by: this.fg.get('referredBy')?.value || 0,
      medical_alert: this.fg.get('medicalAlert')?.value,
      note: this.fg.get('notes')?.value,
      taxnumber: this.fg.get('taxNumber')?.value,
      migration_id: this.fg.get('migrationId')?.value,
      organisation: this.fg.get('organisation')?.value?.value || 0,
      national_id: this.fg.get('national_id')?.value,
      promotional_notification_channels_allowed:  Array.from(this.promotionPreferenceMap.values()).map((o: { code: any; }) => o.code),
      is_child: this.fg.get('is_child')?.value,
      parent_client_id: this.fg.get('is_child')?.value ? this.fg.get('parent_client')?.value : 0,
      relation: this.fg.get('is_child')?.value ? this.fg.get('relation')?.value?.value : ClientRelation[ClientRelation.None],
      createdin_tenant_id: this.selectedTenant(),
      home_club_tenant_id: this.selectedTenant(),
      client_representative_id: this.fg.get('newRepresentative')?.value ? this.fg.get('newRepresentative')?.value.id : 0,
      country_code: this.country_code(),
      state_code: this.state_code(),
      city_code: this.city_code(),
      image: this.clientPicture_Base64 ? this.clientPicture_Base64 : this.clientById()?.image,
      nationality: this.fg.get('nationality')?.value || null,
    };

    return baseData;
  }

  SetProspectObject(): CreateProspectRequest {
    const client: CreateProspectRequest = {} as CreateProspectRequest;
    client.prospect = {
      full_name: this.NameControl.value,
      mobile_country_code: this.MobileNoControl.value
        ? this.MobileNoControl.value.dialCode
        : '',
      mobile_number: this.MobileNoControl.value
        ? this.MobileNoControl.value.number.replaceAll(/[\s\-()]/g, '')
        : 0,
      email_id: this.EmailIdControl.value,
      date_of_birth: this.DateOfBirthControl.value ? 
        this.datePipe.transform(this.DateOfBirthControl.value, 'yyyy-MM-dd') : null,
      gender: this.GetSelectedGenderFromForm(),
      location: '',
      second_mobile_country_code: this.SecondMobileNoControl.value ? this.SecondMobileNoControl.value.dialCode : '',
      second_mobile_number: this.SecondMobileNoControl.value ? this.SecondMobileNoControl.value.number.replaceAll(/[\s\-()]/g, '') : null,
      preferred_language: this.PreferredLanguageControl.value?.LANG_CODE || '',
      place_id: null,
      address: this.AddressControl.value || '',
      state_code: this.state_code() || '',
      city_code: this.city_code() || '',
      country_code: this.country_code() || '',
      address_pin_code: this.AddressPinCodeControl.value || 0,
      emergency_contact_number: this.EmergencyNumberControl.value? this.EmergencyNumberControl.value?.e164Number || '' : '',
      emergency_contact_person: this.EmergencyContactControl.value || '',
      source: this.SourceControl.value,
      channel: this.ChannelControl.value.channel,
      heartrate_monitoring_id: null,
      referred_by: this.SelectedReferredBy.value ? String(this.SelectedReferredBy.value) : "",
      medical_alert: this.MedicalAlertControl.value || '',
      note: this.NotesControl.value || '',
      taxnumber: this.TaxNumberControl.value || '',
      custom_fields: null,
      current_keyfob: null,
      client_representative_id: this.NewRepresentative.value.id,
      createdin_tenant_id: this.selectedTenant(),
      place_id_request: null,
      migration_id: this.MigrationIdControl.value || '',
      image_base64: this.clientPicture_Base64,
      password: '',
      organisation_id: this.organizationFormControl?.value ? this.organizationFormControl.value.value : 0,
      promotional_notification_channels_allowed:  Array.from(this.promotionPreferenceMap.values()).map((o: { code: any; }) => o.code),
      is_child: this.IsChildClientFormControl.value,
      parent_client_id: this.IsChildClientFormControl.value ? this.LinkedClientIdControl.value : 0,
      relation: this.IsChildClientFormControl.value ? this.RelationshipControl.value.value : ClientRelation.None,
      nationality: this.NationalityControl.value || null,
      promotional_sms: false,
      promotional_email: false,
      promotional_phone: false,
      contact_id: 0
    } as CreateClientRequest;

    const formType = this.formType();
    if (this.CreateTask) {  
      if(this.CreateOpportunity )
      {
       client.opportunity = {
        name: client.prospect.full_name,
        stage_master_id: this.stageMasterId(),
        service_type_ids: this.InterestedInIds.length > 0 ? this.InterestedInIds : this.serviceTypes().filter((serviceType: any) => serviceType.behaviour.toLowerCase() =='membership').map((serviceType: any) => serviceType.id),
        value_in_minor_currency: 0,
        expected_closure_date: this.TaskOnControl.value?.value?.toLowerCase() === 'custom date' ?  this.timeService.ConvertLocalDateTimeToUTC(this.TaskOnDateControl.value) : this.TaskOnControl.value?.date(), // set null after venky API update
        assigned_to_staff_id: client.prospect.client_representative_id,
        comments: '',
        hot_warm_cold: OpportunitiesType.Hot,
      } as CreateOpportunityOnClientRequest;
    }

      client.task = {
        assigned_to_type: TaskAssignToType.ClientRep,
        assigned_to_staff_id: null,
        type: this.TaskTypeControl.value?.name,
        category_id: this.taskCategoryId(),
        title: client.prospect.full_name,
        notes: this.Notes || 'Task created at the time of prospect creation',
        priority: TaskPriority.High,
        call_id: null,
        email_id: null,
        sms_id: null,
        scheduled_date: this.timeService.ConvertLocalDateTimeToUTC(this.TaskOnControl.value?.value?.toLowerCase() === 'custom date' ?  this.TaskOnDateControl.value : this.TaskOnControl.value?.date())
      } as CreateTaskByOpportunityOnClientRequest;
    }
    if(formType === 'walk-in-tour' && this.CreateTour)
    {
      const rowValues = this.fg.get('tour_completed')?.value;
      client.tour_completed = {
        outcome: rowValues.tour_outcome.value,
        scheduled_datetime: this.timeService.ConvertLocalDateTimeToUTC(rowValues.scheduled_datetime),
        assigned_to_staff_id: rowValues.assigned_to_staff_id ? rowValues.assigned_to_staff_id.id : 0,
        notes: rowValues.notes
      }
      if(rowValues.tour_outcome.shortname === 'booktrial' &&this.fg.contains('trial_membership'))
      {
        if(this.fg.get('selected_trial_type') && this.fg.get('selected_trial_type')?.value === 'membership')
        {
          const trialValues = this.fg.get('trial_membership')?.value;
          client.trial_membership = {
            trial_start_date: this.timeService.ConvertLocalDateTimeToUTC(trialValues.trial_start_date),
            notes: trialValues.notes,
            trial_preferred_time: this.timeService.GetUTCTimeFromDate(trialValues.trial_preferred_time),
            trial_owner_staff_id: trialValues.trial_owner_staff_id || 0
          }
          client.trial_classes = null;
        }
        else if(this.fg.get('selected_trial_type') && this.fg.get('selected_trial_type')?.value === 'class')
        {
          const trialValues = this.fg.get('trial_classes')?.value;
          client.trial_classes = {trial_classes:trialValues};
          client.trial_membership = null;
        }
        else{
          client.trial_classes = null;  
          client.trial_membership = null;
        }
      }
      else if(rowValues.tour_outcome.shortname === 'notintrested')
      {
        client.opportunity = {
          name: client.prospect.full_name,
          stage_master_id: this.stageMasterId(),
          service_type_ids: this.serviceTypes().filter((serviceType: any) => serviceType.behaviour.toLowerCase() =='membership').map((serviceType: any) => serviceType.id),
          value_in_minor_currency: 0,
          expected_closure_date: this.TaskOnControl.value.value.toLowerCase() === 'custom date' ?  this.timeService.ConvertLocalDateTimeToUTC(this.TaskOnDateControl.value) : this.TaskOnControl.value.date(), // set null after venky API update
          assigned_to_staff_id: client.prospect.client_representative_id,
          comments: rowValues.notes,
          hot_warm_cold: OpportunitiesType.Cold,
        } as CreateOpportunityOnClientRequest;
        client.trial_classes = null;  
        client.trial_membership = null;
      }

      else{
        client.trial_classes = null;  
        client.trial_membership = null;
      } 

    }

    if(formType === 'walk-in-trial' && this.CreateTrial)
    {
        if(this.fg.get('selected_trial_type') && this.fg.get('selected_trial_type')?.value === 'membership')
        {
          const trialValues = this.fg.get('trial_membership')?.value;
          client.trial_membership = {
            trial_start_date: this.timeService.ConvertLocalDateTimeToUTC(trialValues.trial_start_date),
            notes: trialValues.notes,
            trial_preferred_time: this.timeService.GetUTCTimeFromDate(trialValues.trial_preferred_time),
            trial_owner_staff_id: trialValues.trial_owner_staff_id ? trialValues.trial_owner_staff_id.id : 0
          }
          client.trial_classes = null;
        }
        else if(this.fg.get('selected_trial_type') && this.fg.get('selected_trial_type')?.value === 'class')
        {
          const trialValues = this.fg.get('trial_classes')?.value;
          client.trial_classes = {trial_classes:trialValues};
          client.trial_membership = null;
        }
        else{
          client.trial_classes = null;  
          client.trial_membership = null;
        }

    }

    if(formType === 'walk-in-member' && this.CreateMember)
    {
      const tomorrowDate = new Date();
      tomorrowDate.setDate(tomorrowDate.getDate() + 1);
      client.task = {
        assigned_to_type: TaskAssignToType.ClientRep,
        assigned_to_staff_id: null,
        type: 'Tour',
        category_id: this.taskCategoryId(),
        title: client.prospect.full_name,
        notes: 'New Lead Followup',
        priority: TaskPriority.High,
        call_id: null,
        email_id: null,
        sms_id: null,
        scheduled_date: this.timeService.ConvertLocalDateTimeToUTC(tomorrowDate),
      } as CreateTaskByOpportunityOnClientRequest;
    }

    if(formType === 'contact')
    {
      client.prospect.contact_id = this.contactData().contact_id;
    }
    return client;
  }

  GetClientReferrel(client: any) {
    this.fg.get('referredBy')?.setValue(client.clt_id);
  }

  getParentClientId(client: any) {
    this.fg.get('parent_client')?.setValue(client.clt_id);
    if(this.existClientForMobile && this.existClientForMobile.clt_id === client.clt_id)
    {
        const currentErrors = { ...this.MobileNoControl.errors };
        if(currentErrors) delete currentErrors['mobileExists'];
        this.MobileNoControl.setErrors(currentErrors);
        this.mobileAlreadyExists.set(false);
    }
    else if(this.existClientForEmail && this.existClientForEmail.clt_id === client.clt_id)
    {
        const currentErrors = { ...this.EmailIdControl.errors };
        if(currentErrors) delete currentErrors['emailExists'];
        delete currentErrors['emailExists'];
        this.EmailIdControl.setErrors(currentErrors);
        this.emailAlreadyExists.set(false);
    }
    else{
     if(this.existClientForMobile || this.existClientForEmail)
     {
       if(this.clientExistOnMobile())
       {
        this.MobileNoControl.setErrors({mobileExists:true});
        this.mobileAlreadyExists.set(true);
       }
       if(this.clientExistOnEmail())
       {
        this.EmailIdControl.setErrors({emailExists:true});
        this.emailAlreadyExists.set(true);
       }
    }
   }
  }


  removeClient(_event: any) {
    this.fg.get('referredBy')?.setValue(0);
  }

  removeParentClient(_event: any) {
    this.fg.get('parent_client')?.setValue(0);
    if(this.existClientForMobile || this.existClientForEmail)
      {
        if(this.existClientForMobile)
          {
            if(this.clientExistOnMobile())
            {
             this.MobileNoControl.setErrors({mobileExists:true});
             this.mobileAlreadyExists.set(true);
            }
         }
         if(this.existClientForEmail)
         {
          if(this.clientExistOnEmail())
            {
             this.EmailIdControl.setErrors({emailExists:true});
             this.emailAlreadyExists.set(true);
            }
         }
      }
  }


  SetPrefersLanguage(langCode: string) {
    return this.parseSupportedLang.find(
      (lang) => lang.LANG_CODE.toLowerCase() == langCode.toLowerCase()
    );
  }

  handleFileSelect(files: NgxFileDropEntry[]) {
    const file: NgxFileDropEntry = files[0];
    if (file.fileEntry.isFile) {
      const fileEntry = file.fileEntry as FileSystemFileEntry;
      fileEntry.file((file: File) => {
        this.showCropper(file);
      });
    }
  }

  showCropper(imageFile: any) {
    const header: string = 'common.IMAGE_CROP.CLIENT_IMAGE';
    this.refCropper = this.dialogService.open(ImageCropDialogComponent, {
      showHeader: false,
      baseZIndex: 10000,
      width: '70%',
      contentStyle: { 'max-height': '500px', 'max-width': '800px' },
      data: { imageFile, header },
    });
    if (this.refCropper) {
    this.refCropper.onClose.subscribe((croppedImage: any) => {
      if (croppedImage) {
        this.imageBase64 = croppedImage.image;
        this.fg.get('image')?.setValue(croppedImage.image);
        if(this.formEvent() === 'update')
        {
          this.fg.get('image')?.markAsTouched();
          this.fg.get('image')?.updateValueAndValidity();
          this.fg.updateValueAndValidity();
        } 
        this.uploadProfilePicture = false;
        this.uploadSuccess.set(true);
      }
    });
    }
  }

  saveProfilePicture(event: boolean) {
    if(event && this.imageBase64) {
      this.fg.get('image')?.setValue(this.imageBase64);
      if(this.formEvent() === 'update')
      {
        this.fg.get('image')?.markAsTouched();
        this.fg.get('image')?.updateValueAndValidity();
        this.fg.markAsDirty();
        this.fg.updateValueAndValidity();
      } 
      this.uploadProfilePicture = false;
      this.uploadSuccess.set(true);
    }
    else {
      this.uploadProfilePicture = false;
      this.uploadSuccess.set(false);
      if(this.fg.get('image')?.value !== '') this.imageBase64 = '';
      this.cdr.detectChanges();
    }
  }

  // Helper methods for form data handling
  private findChannel(channel: string): any {
    if (!channel) return null;
    return this.channelCollection.find(
      (c) => c.channel?.toLowerCase() === channel.toLowerCase()
    );
  }

  private getSource(client: ClientById): string {
    if (this.isOtherChannel(client.channel)) {
      return client.source || '';
    }
    const channel = this.findChannel(client.channel);
    return channel?.source.find(
      (s: any) => s.toLowerCase() === client.source.toLowerCase()
    ) || '';
  }

  private findOrganization(organisation: any): Option | null {
    if (!organisation) return null;
    return this.organizations().find(o => o.value === organisation.id) || null;
  }

  private getPromotionalPreferences(client: any): string[] {
    const data= this.promotionalPreferenceCollection;
    return client.promotional_notification_channels_allowed && 
           client.promotional_notification_channels_allowed.length > 0 ? 
           data.map((o: any) => client.promotional_notification_channels_allowed.includes(o.code) ? o.code : null): 
      [];
  }

  private setupPromotionalPreferences(client: any | null): void {
    const data= this.promotionalPreferenceCollection;
    if (client && client.promotional_notification_channels_allowed?.length > 0) {
      const promotionalPreferences:any[] = [];
      client.promotional_notification_channels_allowed
        .filter((o: string) => o !== 'None')
        .forEach((o: string) => {
          promotionalPreferences.push(data.find((p: any) => p.code === o));
          if(o.toLowerCase() === 'email'){
            this.fg.get('email')?.setValue(true);
          }
          if(o.toLowerCase() === 'whatsapp'){
            this.fg.get('whatsapp')?.setValue(true);
          }
          if(o.toLowerCase() === 'sms'){
            this.fg.get('sms')?.setValue(true);
          }
          if (o.toLowerCase() === 'push' || o.toLowerCase() === 'mobilenotification') {
            this.fg.get('push')?.setValue(true);
          }
        });

      promotionalPreferences.forEach((o: any) => {
        if (o) {
          this.promotionPreferenceMap.set(o.name, o);
        }
      });
      this.PromotionalPreferenceControl.setValue(Array.from(this.promotionPreferenceMap.values()).map((o: { code: any; }) => o.code));
      this.PromotionalPreferenceControl.markAsDirty();
      this.PromotionalPreferenceControl.updateValueAndValidity();
      this.cdr.detectChanges();
    }
    else {
      this.promotionalPreferenceCollection.forEach((o: any) => {
        this.promotionPreferenceMap.set(o.name, o);
      });
      this.PromotionalPreferenceControl.setValue(
        Array.from(this.promotionPreferenceMap.values()).map((o: { code: string }) => o.code),
      );
      this.fg.get('sms')?.setValue(true);
      this.fg.get('whatsapp')?.setValue(true);
      this.fg.get('email')?.setValue(true);
      this.fg.get('push')?.setValue(true);
      this.cdr.detectChanges();
    }
  }

  private isOtherChannel(channel: string): boolean {
    if (!channel) return false;
    return channel.toLowerCase() === ClientChannel[ClientChannel.Other].toLowerCase() || 
    channel.toLowerCase() === 'other';
  }

  markFormGroupTouched(formGroup: UntypedFormGroup): void {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      if (control instanceof UntypedFormGroup || control instanceof UntypedFormArray) {
        this.markFormGroupTouched(control as UntypedFormGroup);
      } else {
        control?.markAsTouched({ onlySelf: true });
      }
    });
  }

  closeSidebar(event:boolean=false) {
    // Emit event to let parent update the input
    this.hideProspect.emit(this.clientId() || 0);
    this.closeEditProfile.emit(event);
    
    // Clean up any lingering PrimeNG overlay elements after drawer animation completes
    setTimeout(() => {
      // Remove drawer mask/overlay elements
      const overlays = document.querySelectorAll('.p-drawer-mask, .p-component-overlay, .p-drawer-mask-leave');
      overlays.forEach(overlay => {
        overlay.remove();
      });
      
      // Re-enable body scroll and remove overflow hidden
      document.body.classList.remove('p-overflow-hidden');
      document.body.style.removeProperty('overflow');
      
      // Force final change detection
      this.cdr.detectChanges();
    }, 300);
  }

  selectTenant(event: any) {
    this.fg.get('tenant_id')?.setValue(event.id);
    this.selectedTenant.set(event.id);
    this.loadClientGenderOptions(event.id);
    this.cdr.detectChanges();
  }

  getNationalityLocalization(): Observable<any> {
    return this.commonService.GetNationalidLocalization();
  }


  removePicture() {
    this.fg.get('image')?.setValue(null);
  }

  closeLostDeal(_event:any) {
    this.showChooseReason.set(false);
    this.closeSidebar();
  }

  openClientSidebar(clientId:number) {
   this.GoToClientDetails.emit({clientId:clientId});
  }

  setBtnDisbale()
  {
    if(this.formEvent() === 'create')
    {
      return false;
    }
    else
    {
      // In edit mode, enable button if:
      // 1. Form is dirty (has changes) OR
      // 2. Form is touched (user interacted with it) OR
      // 3. There are actual value changes detected
      return !(this.fg.dirty || this.fg.touched || this.hasActualChanges());
    }
  }

  /**
   * Check if there are actual value changes compared to original client data
   * This ensures button is enabled when user makes real changes
   */
  private hasActualChanges(): boolean {
    const clientById = this.clientById();
    if (!clientById || this.formEvent() !== 'update') {
      return false;
    }

    const updateData = this.SetClientUpdateObject();
    const patchObject = this.GetClientPatchObject(updateData, clientById);
    
    return patchObject && patchObject.length > 0;
  }

  get clientPicture_Base64(): string {
    return this.fg.get('image')?.value
      ? this.fg.get('image')?.value.replaceAll('data:image/png;base64,', '')
      : '';
  }

  // Form Control Getters
  get NameControl(): UntypedFormControl {
    return this.fg.get('full_name') as UntypedFormControl;
  }


  get EmailIdControl(): UntypedFormControl {
    return this.fg.get('emailId') as UntypedFormControl;
  }

  get MobileNoControl(): UntypedFormControl {
    return this.fg.get('mobileNo') as UntypedFormControl;
  }

  get ChannelControl(): UntypedFormControl {
    return this.fg.get('channel') as UntypedFormControl;
  }

  get SourceControl(): UntypedFormControl {
    return this.fg.get('source') as UntypedFormControl;
  }

  get PromotionalPreferenceControl(): UntypedFormControl {
    return this.fg.get('promotionalPreference') as UntypedFormControl;
  }

  get CreateTaskControl(): UntypedFormControl {
    return this.fg.get('createTask') as UntypedFormControl;
  }

  get TaskTypeControl(): UntypedFormControl {
    return this.fg.get('taskType') as UntypedFormControl;
  }

  get TaskOnControl(): UntypedFormControl {
    return this.fg.get('taskOn') as UntypedFormControl;
  }

  get TaskOnDateControl(): UntypedFormControl {
    return this.fg.get('taskOnDate') as UntypedFormControl;
  }

  get InterestedInControl(): UntypedFormControl {
    return this.fg.get('interestedIn') as UntypedFormControl;
  }

  get SelectedGender(): UntypedFormControl {
    return this.fg.get('selectedGender') as UntypedFormControl;
  }

  get SelectedReferredBy(): UntypedFormControl {
    return this.fg.get('referredBy') as UntypedFormControl;
  }

  get NewRepresentative(): UntypedFormControl {
    return this.fg.get('newRepresentative') as UntypedFormControl;
  }

  get organizationFormControl(): UntypedFormControl {
    return this.fg.get('organization') as UntypedFormControl;
  }

  get CreateTask(): boolean {
    return this.fg.get('createTask')?.value as boolean;
  }

  get CreateOpportunity(): boolean {
    return this.fg.get('createOpportunity')?.value as boolean;
  }

  get CreateTour(): boolean {
    return this.fg.get('createWalkInTour')?.value as boolean;
  }

  get CreateTrial(): boolean {
    return this.fg.get('createWalkInTrial')?.value as boolean;
  }

  get CreateMember(): boolean {
    return this.fg.get('createWalkInMember')?.value as boolean;
  }

  get Source(): string[] {
    const source = this.ChannelControl.value?.source;
    // Ensure we always return an array
    return Array.isArray(source) ? source : [];
  }

  get DisableSource(): boolean {
    return !(this.Source && this.Source.length > 0);
  }

  get TaskOnDate(): Date {
    return this.TaskOnDateControl.value;
  }

  get InterestedInIds(): number[] {
    return this.InterestedInControl.value?.length > 0
      ? this.InterestedInControl.value.map((item: Option) => item.value)
      : [];
  }

  get Notes(): any {
    return this.fg.get('notes')?.value;
  }

  get DateOfBirthControl(): UntypedFormControl {
    return this.fg.get('dateOfBirth') as UntypedFormControl;
  }

  get EmergencyContactControl(): UntypedFormControl {
    return this.fg.get('emergencyContact') as UntypedFormControl;
  }

  get EmergencyNumberControl(): UntypedFormControl {
    return this.fg.get('emergencyNumber') as UntypedFormControl;
  }

  get MedicalAlertControl(): UntypedFormControl {
    return this.fg.get('medicalAlert') as UntypedFormControl;
  }

  get SecondMobileNoControl(): UntypedFormControl {
    return this.fg.get('secondMobileNo') as UntypedFormControl;
  }

  get PreferredLanguageControl(): UntypedFormControl {
    return this.fg.get('preferredLanguage') as UntypedFormControl;
  }

  get AddressPinCodeControl(): UntypedFormControl {
    return this.fg.get('address_pin_code') as UntypedFormControl;
  }

  get AddressControl(): UntypedFormControl {
    return this.fg.get('address') as UntypedFormControl;
  }

  get NationalIdControl(): UntypedFormControl {
    return this.fg.get('national_id') as UntypedFormControl;
  }

  get TaxNumberControl(): UntypedFormControl {
    return this.fg.get('taxNumber') as UntypedFormControl;
  }

  get MigrationIdControl(): UntypedFormControl {
    return this.fg.get('migrationId') as UntypedFormControl;
  }

  get NotesControl(): UntypedFormControl {
    return this.fg.get('notes') as UntypedFormControl;
  }

  get LinkedClientIdControl(): UntypedFormControl {
    return this.fg.get('parent_client') as UntypedFormControl;
  }

  get RelationshipControl(): UntypedFormControl {
    return this.fg.get('relation') as UntypedFormControl;
  }

  get IsChildClientFormControl(): UntypedFormControl {
    return this.fg.get('is_child') as UntypedFormControl;
  }

  get PreferedLanguageFormControl(): UntypedFormControl {
    return this.fg.get('preferredLanguage') as UntypedFormControl;
  }

  get NationalityControl(): UntypedFormControl {
    return this.fg.get('nationality') as UntypedFormControl;
  }

  // Computed properties
  get TenantIdControl(): UntypedFormControl {
    return this.fg.get('tenant_id') as UntypedFormControl;
  }

  getbuttonText() {
    return this.clientData() ? 'EDIT_CLIENT_PROFILE.SAVE_CHANGES' : 'Shared.CREATE-PROSPECT.BUTTON_CREATEPROFILE';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

}
